import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ScreeningConfigurationService } from '../services/screening-configuration.service';
import { ScreeningConfigStateService } from '../../core/screening-config-state.service';

@Injectable({ providedIn: 'root' })
export class BulkSearchGuard implements CanActivate {
  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private screeningConfigService: ScreeningConfigurationService,
    private screeningConfigState: ScreeningConfigStateService,
  ) {}

  canActivate(): boolean | UrlTree | Observable<boolean | UrlTree> {
    // On the server, skip the feature-flag check to avoid localStorage access
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    // Signal navigation that bulk-search feature resolution is in progress
    try {
      this.screeningConfigState.setBulkSearchGuardLoading(true);
    } catch {
      // non-fatal if state service is not ready
    }

    // Always fetch latest configuration to ensure we respect backend flags.
    // If this becomes too chatty, we can later cache based on state service.
    return this.screeningConfigService.filterPaginated({ query: {} }).pipe(
      map((response: any) => {
        let enabled = false;

        if (response && response.result && response.result.length > 0) {
          const config = response.result[0];
          this.screeningConfigState.setConfig(config);
          enabled = !!config.batchScreeningEnable;
        } else {
          this.screeningConfigState.reset();
        }

        // Guard finished; clear loading flag
        try {
          this.screeningConfigState.setBulkSearchGuardLoading(false);
        } catch {}

        if (enabled) {
          return true;
        }

        // If batch screening is disabled, redirect user to individual search
        return this.router.parseUrl('/busqueda-individual');
      }),
      catchError(() => {
        // On any error, fail closed and send user to individual search
        try {
          this.screeningConfigState.setBulkSearchGuardLoading(false);
        } catch {}
        return of(this.router.parseUrl('/busqueda-individual'));
      }),
    );
  }
}
